import { ChangeDetectionStrategy, Component, computed, HostBinding, input } from '@angular/core';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'custom';

@Component({
  selector: 'ol-avatar',
  standalone: true,
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarComponent {
  readonly src = input<string>();
  readonly alt = input('Avatar');
  readonly size = input<AvatarSize>('md');
  readonly rounded = input(false);
  readonly badge = input<'star' | null>(null);
  readonly fallbackSrc = input('assets/img/profile/avatar.webp');

  /** Custom dimensions — only used when size is 'custom' */
  readonly width = input<string>();
  readonly height = input<string>();
  readonly borderRadius = input<string>();

  readonly imageSrc = computed(() => this.src() || this.fallbackSrc());

  readonly imageStyles = computed(() => {
    const styles: Record<string, string> = {};
    if (this.size() === 'custom' && this.borderRadius()) {
      styles['border-radius'] = this.borderRadius()!;
    }
    return styles;
  });

  @HostBinding('class')
  get hostClasses(): string {
    const classes = [`avatar--${this.size()}`];
    if (this.rounded()) classes.push('avatar--rounded');
    return classes.join(' ');
  }

  @HostBinding('style.width')
  get hostWidth(): string | undefined {
    if (this.size() !== 'custom') return undefined;
    const w = this.width();
    // Skip percentage values — let parent CSS control sizing
    return w && !w.endsWith('%') ? w : undefined;
  }

  @HostBinding('style.height')
  get hostHeight(): string | undefined {
    if (this.size() !== 'custom') return undefined;
    const h = this.height();
    return h && !h.endsWith('%') ? h : undefined;
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target.src !== this.fallbackSrc()) {
      target.src = this.fallbackSrc();
    }
  }
}
